import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import { useAuth } from '../contexts/AuthContext';
import ConditionalNavBar from '../components/ConditionalNavBar';
import propertyData from '../data/student_properties.json';

const PropertySearchResultsJSON = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get search parameters from location state
    const params = location.state?.searchParams;
    
    if (!params) {
      // If no search params, redirect back to home
      navigate('/json');
      return;
    }

    setSearchParams(params);
    
    // Filter properties based on search criteria
    const initialFiltered = filterProperties(params);
    setAllProperties(initialFiltered);
    setFilteredProperties(initialFiltered);
    setLoading(false);
  }, [location.state, navigate]);

  // Real-time filtering when amenities or price range changes
  useEffect(() => {
    if (allProperties.length > 0) {
      let filtered = [...allProperties];

      // Filter by selected amenities
      if (selectedAmenities.length > 0) {
        filtered = filtered.filter(property =>
          selectedAmenities.every(amenity =>
            property.amenities.includes(amenity)
          )
        );
      }

      // Filter by price range
      filtered = filtered.filter(property =>
        property.price_per_month >= priceRange.min && property.price_per_month <= priceRange.max
      );

      setFilteredProperties(filtered);
    }
  }, [selectedAmenities, priceRange, allProperties]);

  const filterProperties = (params) => {
    // Extract properties array from JSON structure
    let results = propertyData.properties ? [...propertyData.properties] : [];
    
    console.log('=== PROPERTY SEARCH DEBUG ===');
    console.log('Initial properties count:', results.length);
    console.log('Search params:', params);
    console.log('Sample property structure:', results[0]);
    console.log('Available campus_ids in JSON:', [...new Set(results.map(p => p.campus_id))]);
    console.log('Available university_ids in JSON:', [...new Set(results.map(p => p.university_id))]);

    // For JSON testing: if campus/institution don't exist in data, show all properties
    const campusExists = results.some(p => p.campus_id === parseInt(params.campus));
    const institutionExists = results.some(p => p.university_id === parseInt(params.institution));
    
    console.log('Campus exists in JSON:', campusExists);
    console.log('Institution exists in JSON:', institutionExists);

    // Only filter by campus if it exists in the JSON data
    if (params.campus && campusExists) {
      console.log('Filtering by campus:', params.campus, 'type:', typeof params.campus);
      const beforeCount = results.length;
      results = results.filter(property => {
        const match = property.campus_id === parseInt(params.campus);
        if (!match && beforeCount < 5) {
          console.log(`Property ${property.id} campus_id ${property.campus_id} !== ${parseInt(params.campus)}`);
        }
        return match;
      });
      console.log('After campus filter:', results.length, 'properties');
    } else if (params.campus && !campusExists) {
      console.log('⚠️ Campus', params.campus, 'not found in JSON data. Showing all properties for testing.');
    }

    // Only filter by institution if it exists in the JSON data
    if (params.institution && institutionExists) {
      console.log('Filtering by institution:', params.institution);
      results = results.filter(property => 
        property.university_id === parseInt(params.institution)
      );
      console.log('After institution filter:', results.length, 'properties');
    } else if (params.institution && !institutionExists) {
      console.log('⚠️ Institution', params.institution, 'not found in JSON data. Showing all properties for testing.');
    }

    // Filter by room type (check accommodation_details.room_types array)
    if (params.roomType) {
      console.log('Filtering by room type:', params.roomType);
      results = results.filter(property => {
        const roomTypes = property.accommodation_details?.room_types || [];
        const searchType = params.roomType.toLowerCase();
        
        console.log(`Property ${property.id} room_types:`, roomTypes);
        
        // Map search terms to JSON terms
        if (searchType === 'private') {
          return roomTypes.includes('single');
        } else if (searchType === 'shared') {
          return roomTypes.includes('shared');
        } else if (searchType === 'studio') {
          // Studio would be single with private bathroom
          return roomTypes.includes('single') && 
                 property.accommodation_details?.bathroom_type === 'single';
        }
        return true;
      });
      console.log('After room type filter:', results.length, 'properties');
    }

    // Filter by bathroom type (check accommodation_details.bathroom_type)
    if (params.bathroomType) {
      console.log('Filtering by bathroom type:', params.bathroomType);
      results = results.filter(property => {
        const bathType = property.accommodation_details?.bathroom_type || '';
        const searchBath = params.bathroomType.toLowerCase();
        
        console.log(`Property ${property.id} bathroom_type:`, bathType);
        
        // Map search terms: 'Private' -> 'single', 'Shared' -> 'shared'
        if (searchBath === 'private') {
          return bathType === 'single';
        } else if (searchBath === 'shared') {
          return bathType === 'shared';
        }
        return true;
      });
      console.log('After bathroom filter:', results.length, 'properties');
    }

    // Filter by gender (check accommodation_details.gender_preference)
    if (params.gender) {
      console.log('Filtering by gender:', params.gender);
      results = results.filter(property => {
        const genderPref = property.accommodation_details?.gender_preference || '';
        const searchGender = params.gender.toLowerCase();
        
        console.log(`Property ${property.id} gender_preference:`, genderPref);
        
        // Map terms: 'male' -> 'male_only', 'female' -> 'female_only'
        if (searchGender === 'male') {
          return genderPref === 'male_only' || genderPref === 'mixed';
        } else if (searchGender === 'female') {
          return genderPref === 'female_only' || genderPref === 'mixed';
        }
        return true;
      });
      console.log('After gender filter:', results.length, 'properties');
    }

    console.log('Final results count:', results.length);
    console.log('=== END DEBUG ===');
    return results;
  };

  // Get unique amenities from all filtered properties
  const getUniqueAmenities = () => {
    const amenitiesSet = new Set();
    allProperties.forEach(property => {
      property.amenities?.forEach(amenity => amenitiesSet.add(amenity));
    });
    return Array.from(amenitiesSet).sort();
  };

  const handleAmenityToggle = (amenity) => {
    setSelectedAmenities(prev => 
      prev.includes(amenity)
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const handleViewDetails = (property) => {
    // For now, just log the property details
    console.log('View details for property:', property);
  };

  const handleBookProperty = (property) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      alert('Please log in to book a property');
      navigate('/Login');
      return;
    }

    // Navigate to booking page with property and search dates
    navigate('/property-booking', {
      state: {
        property: property,
        searchParams: searchParams,
        bookingDates: {
          checkIn: searchParams.checkIn,
          checkOut: searchParams.checkOut
        }
      }
    });
  };

  const clearFilters = () => {
    setSelectedAmenities([]);
    setPriceRange({ min: 0, max: 10000 });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <ConditionalNavBar />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', color: '#6c757d' }}>Loading properties...</p>
        </div>
      </div>
    );
  }

  const uniqueAmenities = getUniqueAmenities();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <ConditionalNavBar />
      
      {/* Top login prompt shown when user is not authenticated */}
      {!isAuthenticated && (
        <div className="container mt-3">
          <div className="alert alert-info mb-0" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            Please login to book a property with your accommodation voucher
          </div>
        </div>
      )}
      
      <div style={{ padding: '40px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Search Summary */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '30px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginBottom: '16px', color: '#2c3e50' }}>
              Available Properties (JSON Version)
            </h2>
            <p style={{ color: '#6c757d', marginBottom: '16px' }}>
              Showing {filteredProperties.length} of {allProperties.length} properties
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {searchParams.campus && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Campus:</strong> {searchParams.campus}
                </div>
              )}
              {searchParams.checkIn && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Check-in:</strong> {searchParams.checkIn}
                </div>
              )}
              {searchParams.checkOut && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Check-out:</strong> {searchParams.checkOut}
                </div>
              )}
              {searchParams.roomType && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Room Type:</strong> {searchParams.roomType}
                </div>
              )}
              {searchParams.bathroomType && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Bathroom:</strong> {searchParams.bathroomType}
                </div>
              )}
              {searchParams.gender && (
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>Gender:</strong> {searchParams.gender}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '30px' }}>
            {/* Filters Sidebar */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              height: 'fit-content',
              position: 'sticky',
              top: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: 0, color: '#2c3e50' }}>Filters</h4>
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline'
                  }}
                >
                  Clear All
                </button>
              </div>

              {/* Amenities Filter */}
              <div style={{ marginBottom: '24px' }}>
                <h6 style={{ marginBottom: '12px', color: '#495057', fontWeight: '600' }}>
                  Amenities
                </h6>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {uniqueAmenities.map(amenity => (
                    <div key={amenity} style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedAmenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontSize: '14px', color: '#495057' }}>{amenity}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range Filter */}
              <div>
                <h6 style={{ marginBottom: '12px', color: '#495057', fontWeight: '600' }}>
                  Price Range (per semester)
                </h6>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', color: '#6c757d', display: 'block', marginBottom: '4px' }}>
                    Min: R{priceRange.min}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10000"
                    step="100"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({
                      ...prev,
                      min: parseInt(e.target.value)
                    }))}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '14px', color: '#6c757d', display: 'block', marginBottom: '4px' }}>
                    Max: R{priceRange.max}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10000"
                    step="100"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({
                      ...prev,
                      max: parseInt(e.target.value)
                    }))}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>

            {/* Properties Grid */}
            <div>
              {filteredProperties.length === 0 ? (
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '40px',
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{ color: '#6c757d', marginBottom: '16px' }}>
                    No properties found
                  </h4>
                  <p style={{ color: '#6c757d' }}>
                    Try adjusting your filters to see more results.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '24px'
                }}>
                  {filteredProperties.map(property => (
                    <PropertyCard 
                      key={property.id} 
                      property={property}
                      onViewDetails={handleViewDetails}
                      onBook={handleBookProperty}
                      isAuthenticated={isAuthenticated}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertySearchResultsJSON;
